import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import IconSelect from '@/components/common/icons/IconSelect';
import InputField from '@/components/common/InputField';
import Modal from '@/components/common/modals/Modal';
import { useToasts } from '@/components/common/Toasts';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import { useMutation } from '@apollo/client';
import { FormLabel } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    CreateTransactionEntryCategoryDocument,
    CreateTransactionEntryCategoryGroupDocument,
    Supercategory,
    TransactionEntryCategoryFieldsFragment,
    TransactionEntryCategoryGroupFieldsFragment,
    UpdateTransactionEntryCategoryDocument,
    UpdateTransactionEntryCategoryGroupDocument,
} from '@common/graphql/generated';
import {
    formatSupercategory,
    SUPERCATEGORIES,
    useTransactionEntryCategories,
} from '@common/react/cashflow';
import { useCategoryGroupOptions } from '@common/react/cashflow/useCategoryGroupOptions';
import { useTransactionEntryCategoryGroups } from '@common/react/cashflow/useTransactionEntryCategoryGroups';
import { IconName } from '@common/react/icons';
import { TOASTS } from '@common/toasts';
import { useState } from 'react';

interface CategoryModalProps {
    onSuccess?: (category: { id: string; name: string }) => void;
    existingCategory?: TransactionEntryCategoryFieldsFragment;
    existingCategoryGroup?: TransactionEntryCategoryGroupFieldsFragment;
}

export const showCategoryModal = (props: CategoryModalProps) =>
    ModalManager.show(CategoryModal, props);

const CategoryModal = ModalManager.create<CategoryModalProps>(
    ({ onSuccess, existingCategory, existingCategoryGroup }) => {
        useTrackModalShown('Category');

        const modal = useModal();
        const {
            transactionEntryCategories,
            transactionEntryCategoriesLoading,
        } = useTransactionEntryCategories();
        const {
            transactionEntryCategoryGroups,
            transactionEntryCategoryGroupsLoading,
        } = useTransactionEntryCategoryGroups();

        const categoryGroupOptions = useCategoryGroupOptions();
        const supercategoryOptions = buildSupercategoryOptions();
        const [categoryIconName, setCategoryIconName] = useState<IconName>(
            existingCategory?.iconName || 'Hexagon02'
        );
        const [categoryGroupIconName, setCategoryGroupIconName] =
            useState<IconName>(
                existingCategoryGroup?.iconName || 'CurrencyDollarCircle'
            );
        const [isEditingCategoryGroup, setIsEditingCategoryGroup] = useState(
            !!existingCategoryGroup
        );
        const [supercategory, setSupercategory] = useState<
            Supercategory | null | undefined
        >(existingCategoryGroup?.supercategory);
        const [categoryGroupName, setCategoryGroupName] = useState<
            string | null | undefined
        >(existingCategoryGroup?.name);
        const [categoryGroupId, setCategoryGroupId] = useState<
            string | null | undefined
        >(existingCategory?.categoryGroup.id);
        const [categoryName, setCategoryName] = useState<
            string | null | undefined
        >(existingCategory?.name);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [createCategory] = useMutation(
            CreateTransactionEntryCategoryDocument
        );
        const [updateCategory] = useMutation(
            UpdateTransactionEntryCategoryDocument
        );
        const [createCategoryGroup] = useMutation(
            CreateTransactionEntryCategoryGroupDocument
        );
        const [updateCategoryGroup] = useMutation(
            UpdateTransactionEntryCategoryGroupDocument
        );
        const { showErrorToast, showSuccessToast } = useToasts();

        const isLoading =
            transactionEntryCategoriesLoading ||
            transactionEntryCategoryGroupsLoading;

        const isDisabled =
            isLoading ||
            (isEditingCategoryGroup
                ? existingCategoryGroup
                    ? !supercategory || !categoryGroupName // When updating a category group, category name isn't presented to the user
                    : !supercategory || !categoryGroupName || !categoryName // When creating a category group, category name _is_ presented to the user
                : !categoryGroupId || !categoryName); // Not editing category group; only groupId and categoryName are required

        const submit = async () => {
            if (isDisabled || isLoading || isSubmitting) return;

            assert(transactionEntryCategoryGroups);

            setIsSubmitting(true);

            let groupId = categoryGroupId;

            if (isEditingCategoryGroup) {
                try {
                    assert(categoryGroupName);
                    assert(supercategory);

                    if (
                        // This check isn't enforced on the backend for default category groups, since the `user_id` of those category groups is `null` (i.e. shared across all users) and the unique constraint covers `(user_id, name)`
                        // TODO: Consider introducing a custom mutation for creating a category group to enforce this via code
                        !existingCategoryGroup &&
                        checkExistingCategoryGroup({
                            categoryGroupName,
                            transactionEntryCategoryGroups,
                        })
                    ) {
                        showErrorToast(TOASTS.categoryNameAlreadyExists);
                        setIsSubmitting(false);
                        return;
                    }

                    if (existingCategoryGroup) {
                        await updateCategoryGroup({
                            variables: {
                                id: existingCategoryGroup.id,
                                name: categoryGroupName,
                                supercategory,
                                iconName: categoryGroupIconName,
                            },
                        });

                        showSuccessToast(TOASTS.updateCategorySuccess);
                        // If we're editing a category group, we're done; don't need to create/update a category
                        modal.remove();
                        return;
                    } else {
                        const { data } = await createCategoryGroup({
                            variables: {
                                name: categoryGroupName,
                                supercategory,
                                iconName: categoryGroupIconName,
                            },
                        });

                        if (
                            data?.createTransactionEntryCategoryGroup
                                ?.transactionEntryCategoryGroup
                        ) {
                            groupId =
                                data.createTransactionEntryCategoryGroup
                                    .transactionEntryCategoryGroup.id;
                        }
                    }
                } catch (e) {
                    logError(e);
                    showErrorToast(
                        TOASTS.createOrUpdateCategoryUnexpectedError
                    );
                    setIsSubmitting(false);
                    return;
                }
            }

            // Safe to assert this - checked for in isDisabled
            assert(categoryName);

            // groupId should be set by now (either via the form, or from the createCategoryGroup mutation)
            assert(groupId);

            if (
                !existingCategory &&
                checkExistingCategory({
                    categoryName,
                    categoryGroupId: groupId,
                    transactionEntryCategories,
                })
            ) {
                showErrorToast(TOASTS.subcategoryNameAlreadyExists);
                setIsSubmitting(false);
                return;
            }

            try {
                if (existingCategory) {
                    const { data } = await updateCategory({
                        variables: {
                            id: existingCategory.id,
                            name: categoryName,
                            categoryGroupId: groupId,
                            iconName: categoryIconName,
                        },
                    });

                    if (
                        data?.updateTransactionEntryCategory
                            ?.transactionEntryCategory
                    ) {
                        const { id, name } =
                            data.updateTransactionEntryCategory
                                .transactionEntryCategory;

                        onSuccess?.({ id, name });
                    }

                    showSuccessToast(TOASTS.updateSubcategorySuccess);
                } else {
                    const { data } = await createCategory({
                        variables: {
                            name: categoryName,
                            categoryGroupId: groupId,
                            iconName: categoryIconName,
                        },
                    });

                    if (
                        data?.createTransactionEntryCategory
                            ?.transactionEntryCategory
                    ) {
                        const { id, name } =
                            data.createTransactionEntryCategory
                                .transactionEntryCategory;

                        onSuccess?.({ id, name });
                    }

                    showSuccessToast(TOASTS.createSubcategorySuccess);
                }

                modal.remove();
            } catch (e) {
                logError(e);
                showErrorToast(TOASTS.createOrUpdateSubcategoryUnexpectedError);
            } finally {
                setIsSubmitting(false);
            }
        };

        const labelProps = { fontSize: 'md' };

        return (
            <Modal
                isOpen={modal.visible}
                onClose={modal.remove}
                closeOnOverlayClick={false}
                size="xl"
                closeOnEsc={false}
                modalBodyProps={{ overflow: 'visible' }}
                modalContentProps={{ h: '90vh', my: 8 }}
                title={
                    existingCategory
                        ? 'Update subcategory'
                        : existingCategoryGroup
                          ? 'Update category'
                          : 'Create category'
                }
                renderFooter={() => (
                    <FlexRow justifyContent="flex-end" px={10} py={5}>
                        <Button
                            onClickEvent="Save Category Button Clicked"
                            onClick={submit}
                            isLoading={isSubmitting}
                            isDisabled={isDisabled}
                        >
                            Save
                        </Button>
                    </FlexRow>
                )}
            >
                <FlexCol gap={5}>
                    {isEditingCategoryGroup ? (
                        <FlexCol gap={5}>
                            {!existingCategoryGroup && (
                                <ArrowLinkText
                                    arrowDirection="left"
                                    onClick={() =>
                                        setIsEditingCategoryGroup(false)
                                    }
                                >
                                    Back to category selection
                                </ArrowLinkText>
                            )}
                            <InputField
                                name="supercategory"
                                type="fancySelect"
                                label="Supercategory"
                                labelProps={labelProps}
                                options={supercategoryOptions}
                                onChange={({ value }) => {
                                    setSupercategory(value);
                                }}
                                value={supercategory}
                                fancySelectOptions={{
                                    isClearable: false,
                                    placeholder: 'Select supercategory...',
                                }}
                            />
                            <FlexCol>
                                <FormLabel {...labelProps}>
                                    Icon & category name
                                </FormLabel>
                                <FlexRow gap={2} align="center">
                                    <IconSelect
                                        value={categoryGroupIconName}
                                        onChange={setCategoryGroupIconName}
                                    />
                                    <InputField
                                        name="categoryGroupName"
                                        labelProps={labelProps}
                                        placeholder="Enter category name"
                                        type="text"
                                        inputProps={{
                                            variant: 'outline',
                                            size: 'md',
                                        }}
                                        value={categoryGroupName}
                                        onChange={setCategoryGroupName}
                                    />
                                </FlexRow>
                            </FlexCol>
                        </FlexCol>
                    ) : (
                        <InputField
                            name="categoryGroup"
                            type="fancySelect"
                            label="Category"
                            labelProps={labelProps}
                            options={categoryGroupOptions}
                            onChange={({ value }) => {
                                setCategoryGroupId(value);
                            }}
                            value={categoryGroupId}
                            fancySelectOptions={{
                                menuWidth: '280px',
                                isClearable: false,
                                cta: {
                                    content: 'Create new category',
                                    iconName: 'Plus',
                                    onClick: () => {
                                        setIsEditingCategoryGroup(true);
                                    },
                                },
                                placeholder: 'Select category...',
                            }}
                        />
                    )}
                    {!existingCategoryGroup && (
                        <FlexCol>
                            <FormLabel {...labelProps}>
                                Icon & subcategory name
                            </FormLabel>
                            <FlexRow gap={2} align="center">
                                <IconSelect
                                    value={categoryIconName}
                                    onChange={setCategoryIconName}
                                />
                                <InputField
                                    name="categoryName"
                                    labelProps={labelProps}
                                    placeholder="Enter subcategory name"
                                    type="text"
                                    inputProps={{
                                        variant: 'outline',
                                        size: 'md',
                                    }}
                                    value={categoryName}
                                    onChange={setCategoryName}
                                />
                            </FlexRow>
                        </FlexCol>
                    )}
                </FlexCol>
            </Modal>
        );
    }
);

const checkExistingCategory = ({
    categoryName,
    categoryGroupId,
    transactionEntryCategories,
}: {
    categoryName: string;
    categoryGroupId: string;
    transactionEntryCategories: TransactionEntryCategoryFieldsFragment[];
}) =>
    transactionEntryCategories.some(
        (category) =>
            category.name === categoryName &&
            category.categoryGroup.id === categoryGroupId
    );

const checkExistingCategoryGroup = ({
    categoryGroupName,
    transactionEntryCategoryGroups,
}: {
    categoryGroupName: string;
    transactionEntryCategoryGroups: TransactionEntryCategoryGroupFieldsFragment[];
}) =>
    transactionEntryCategoryGroups.some(
        (categoryGroup) => categoryGroup.name === categoryGroupName
    );

const buildSupercategoryOptions = () =>
    SUPERCATEGORIES.map((supercategory) => ({
        label: formatSupercategory(supercategory),
        value: supercategory,
    }));
